import api from "./api";

/**
 * Service for snapshot-based audit report management.
 * Follows the new architecture: live data is separate from immutable version snapshots.
 */
class AuditReportService {
  async createReport({ siteId, ReportId, templateId, templateName, reportName, projectId }) {
    return api.post(`/audit-reports`, {
      siteId,
      ReportId,
      templateId,
      templateName,
      reportName,
      projectId,
    });
  }

  async updateReport(reportId, payload = {}) {
    return api.put(`/audit-reports/${reportId}`, payload);
  }

  async deleteReport(reportId) {
    return api.delete(`/audit-reports/${reportId}`);
  }

  async getReport(reportId) {
    return api.get(`/audit-reports/${reportId}`);
  }

  async saveReport(reportId) {
    return api.post(`/audit-reports/${reportId}/save`);
  }

  async generateCurrentReport(reportId) {
    return api.post(`/audit-reports/${reportId}/generate`);
  }

  async getReportsBySite(siteId, status = "all") {
    return api.get(`/audit-reports/site/${siteId}`, { params: { status } });
  }

  async getRecentWorkingReports() {
    return api.get(`/audit-reports/recent-working`);
  }

  async downloadCurrentReport(reportId) {
    return api.get(`/audit-reports/${reportId}/download`);
  }

  /**
   * Get the current AuditReport record for a site + template combination.
   * Returns null if no report exists yet (first visit / unsaved draft).
   */
  async getCurrentReport(siteId, templateId, version) {
    return api.get(`/audit-reports/current`, {
      params: { siteId, templateId, version },
    });
  }

  /**
   * Save a version snapshot of the current live data.
   * Creates an AuditReport if one doesn't exist yet, then creates an AuditReportVersion.
   * POST /api/audit-reports/save-version
   */
  async saveVersion({ siteId, templateId, data, siteName, projectId, changes }) {
    return api.post(`/audit-reports/save-version`, {
      siteId,
      templateId,
      data: data || {},
      siteName,
      projectId,
      changes: changes || "Version saved",
    });
  }

  /**
   * Generate a PDF report from the current live data snapshot.
   * Creates an AuditReportVersion with action = "generated-report" and triggers PDF generation.
   * POST /api/audit-reports/generate-report
   */
  async generateReport({ siteId, templateId, versionId, data, siteName, projectId, changes }) {
    return api.post(`/audit-reports/generate-report`, {
      siteId,
      templateId,
      versionId,
      data: data || {},
      siteName,
      projectId,
      changes: changes || "Report generated",
    });
  }



  /**
   * Fetch a complete report with all its related data
   * GET /api/audit-reports/:reportId/complete
   * Returns: { report, siteOverview, works, observations, files }
   */
  async getReportWithAllData(reportId, siteId) {
    return api.get(`/audit-reports/${reportId}/complete`, {
      params: { siteId,reportId },
    });
  }

  /**
   * Fetch all reports with their complete data for dashboard progress
   * GET /api/audit-reports/all-with-data
   * Returns: Array of { report, siteOverview, works, observations, files }
   */
  async getAllReportsWithData() {
    return api.get(`/audit-reports/all-with-data`);
  }

}

export default new AuditReportService();
